import { useEffect } from 'react';
import { useSlotCanvasStore, type SlotExperience } from '@/store/slotCanvasStore';
import { useSlotCanvasContext } from './SlotCanvasContext';

export interface UseRegisterSlotCanvasArgs {
  id: string;
  experience: SlotExperience;
  title: string;
  chatHistoryId?: string;
  /**
   * Open the canvas on mount even while the context signals
   * `suppressAutoOpen` (history hydration / preloaded replay). For
   * host-driven slots — e.g. the report preview bound to the conversation —
   * which must be visible from first render regardless of how the thread
   * was entered. Message-driven behaviour slots leave this off.
   */
  ignoreSuppressAutoOpen?: boolean;
  /**
   * Register the slot without opening it on mount. Used by slots that should be
   * reachable (navigator / an explicit "View") but must not steal the canvas —
   * e.g. artifacts mounted by the briefing panel, which stays open until the
   * user opens an artifact. Defaults to true (auto-open per the flags above).
   */
  autoOpen?: boolean;
  /** Preferred default width (px) for this slot when the user hasn't resized. */
  preferredWidthPx?: number;
  /**
   * Whether this slot renders an in-stream preview chip the user can click to
   * reopen it. Controls whether the canvas frame shows a close (✕) button (vs.
   * collapse-only). Defaults to true — behaviour slots render a chip. Host-driven
   * slots with no chip (report canvas, briefing) must pass false so they can't be
   * closed into an unreachable state.
   */
  hasInStreamCard?: boolean;
}

export interface UseRegisterSlotCanvasResult {
  /** True when this slot is the currently-open one in the canvas. */
  isOpen: boolean;
  /** Opens the canvas to this slot. No-op in inline mode. */
  open: () => void;
}

/**
 * Registers a behaviour slot with the global canvas store and (when appropriate)
 * auto-opens the canvas on mount. Safe to call in both inline and canvas mode —
 * in inline mode it is a no-op.
 */
export function useRegisterSlotCanvas({
  id,
  experience,
  title,
  chatHistoryId,
  ignoreSuppressAutoOpen = false,
  autoOpen = true,
  preferredWidthPx,
  hasInStreamCard = true,
}: UseRegisterSlotCanvasArgs): UseRegisterSlotCanvasResult {
  const context = useSlotCanvasContext();
  const registerSlot = useSlotCanvasStore((s) => s.registerSlot);
  const unregisterSlot = useSlotCanvasStore((s) => s.unregisterSlot);
  const openSlot = useSlotCanvasStore((s) => s.openSlot);
  const isOpen = useSlotCanvasStore((s) => s.openSlotId === id);

  const active = context.mode === 'canvas';
  const suppressAutoOpen = context.suppressAutoOpen;

  // Lifetime: register/unregister and auto-open are tied strictly to the slot
  // id so re-running when metadata changes can't tear the slot down and close
  // the canvas mid-conversation.
  useEffect(() => {
    if (!active) return;
    if (autoOpen && (ignoreSuppressAutoOpen || !suppressAutoOpen)) {
      openSlot(id);
    }
    return () => {
      unregisterSlot(id);
    };
  }, [active, id, autoOpen, ignoreSuppressAutoOpen, suppressAutoOpen, openSlot, unregisterSlot]);

  // Metadata: keep the registered entry in sync (including on mount) when the
  // title/chatHistoryId/experience change — e.g. chat_history_id arrives after
  // the message streams in.
  useEffect(() => {
    if (!active) return;
    registerSlot({
      id,
      experience,
      title,
      chatHistoryId,
      preferredWidthPx,
      hasInStreamCard,
      registeredAt: Date.now(),
    });
  }, [active, id, experience, title, chatHistoryId, preferredWidthPx, hasInStreamCard, registerSlot]);

  return {
    isOpen: active && isOpen,
    open: () => {
      if (active) openSlot(id);
    },
  };
}
